import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { useSpring } from 'react-spring';
import { Flexbox, FlexboxColumn } from '../../components/action/Flexbox';
import { Line } from '../../components/action/Line';
import RedirectButton from '../../components/action/RedirectButton';
import PositionAnalysis from '../../components/Analysis/PositionAnalysis';
import { BlueBadge } from '../../components/Badge/Badge';
import BreadcrumbNavigator from '../../components/BreadcrumbNavigator/BreadcrumbNavigator';
import ChainDropdown from '../../components/Filters/ChainDropdown';
import PaginationTable from '../../components/PaginatedTable/PaginatedTable';
import ProjectInfo from '../../components/ProjectInfo/ProjectInfo';
import ThemedText from '../../components/theme/ThemedText';
import ThemeLogo from '../../components/theme/ThemeLogo';
import { useBackendApi } from '../../helpers/backendHelper';
import { useMrepState } from '../../helpers/mrepHelper';
import { useGlobalState } from '../../state/GlobalStateProvider';
import { filterObject, formatNumber, formatNumberWithCommas, formatString, formatTiers, trackClairtyEvent } from '../../utils';
import './Project.scss';
const Project = () => {
  const { projectName, userAddress } = useParams();
  const [{ protocolMapping }] = useGlobalState();
  const backendHelper = useBackendApi();
  const springProps = useSpring({
    from: { transform: 'translateY(-25px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
    config: { tension: 200, friction: 20 },
  });
  const [{ getIndividualProtocol }] = useMrepState();
  const [projectData, setProjectData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [itemsPerPage] = useState(10);
  const [positionData, setPositioningData] = useState();
  const [currentVicinty, setCurrentVicinity] = useState();
  const [filters, setFilters] = useState();
  const [ranks, setRanks] = useState();

  async function getProtocolMetadata() {
    const metadata = await backendHelper.mrep.getProtocolData(projectName);
    setMetadata(metadata?.data);
  }

  async function getProtocolPositioning(userRank, userAddress) {
    if (!userRank) return setPositioningData([]);
    setPositioningData(null);
    let delta = 0;
    if (+userRank < itemsPerPage / 2) {
      delta = Math.abs(itemsPerPage / 2 - userRank) + 1;
    }
    try {
      const body = {
        address: userAddress,
        filter: {
          protocol: projectName,
          fromRank: Math.max(userRank - itemsPerPage / 2, 1),
          toRank: userRank - 1 + delta + itemsPerPage / 2,
          timeDeltas: ['7d', '30d'],
          ...filterObject(filters || {}),
        },
      };
      const postioninigData = await backendHelper.mrep.getProtocolPositioning(body);

      setPositioningData(postioninigData?.data?.positionings);
      setRanks(postioninigData?.data?.minMaxRanks);
    } catch (error) {}
  }

  function handleVicinty(value) {
    setCurrentVicinity(value);
  }

  function handleFilters(key, value) {
    setFilters({ ...filters, [key]: value });
  }

  useEffect(() => {
    if (projectName && userAddress) {
      getProtocolMetadata();
    }
  }, [projectName, userAddress]);

  useEffect(() => {
    if (projectName && userAddress) {
      getIndividualProtocol(projectName, userAddress, filters).then(res => {
        setProjectData(res?.rankings?.[0]);
        setCurrentVicinity(Math.max(res?.rankings?.[0]?.rank));
        getProtocolPositioning(res?.rankings?.[0]?.rank);
      });

      trackClairtyEvent('user_profile_details', {
        project: projectName,
      });
      
    }
  }, [filters, projectName, userAddress]);

  return (
    <>
      <FlexboxColumn fw style={springProps}>
        <BreadcrumbNavigator />
        <FlexboxColumn fw>
          <Flexbox justifyContent='space-between' fw>
            <FlexboxColumn fw justifyContent='space-between' alignItems={'flex-start'}>
              <ProjectInfo project={protocolMapping?.[projectName]} />
              {metadata ? (
                <Flexbox fw justifyContent='flex-start'>
                  <ThemedText.H5>
                    Total users tracked <ThemedText.Span weight='semi-bold'>{formatNumber(metadata?.trackedUsers)}</ThemedText.Span>
                  </ThemedText.H5>
                  <ThemeLogo icon={'verticalLine'} />
                  <Flexbox gap='0.5rem'>
                    <ThemedText.H5>Actions</ThemedText.H5>
                    {protocolMapping?.[projectName]?.actionTags?.map(tag => (
                      <BlueBadge text={formatString(tag)} />
                    ))}
                  </Flexbox>
                </Flexbox>
              ) : (
                <Flexbox fw justifyContent='flex-start'>
                  <Skeleton height={30} width={500} />
                </Flexbox>
              )}
            </FlexboxColumn>
            <Flexbox gap='0.5rem'>
              <RedirectButton url={protocolMapping?.[projectName]?.website}>
                <ThemedText.H6>Visit App</ThemedText.H6>
              </RedirectButton>
              <ThemeLogo icon={'verticalLine'} />
              <RedirectButton url={protocolMapping?.[projectName]?.twitter}>
                <ThemeLogo icon={'twitterLogo'} />
              </RedirectButton>
            </Flexbox>
          </Flexbox>

          <Line />
          <FlexboxColumn alignItems={'flex-start'} fw gap='1.5rem'>
            <Flexbox fw justifyContent='space-between'>
              <Flexbox gap='0.75rem'>
                <ThemeLogo icon={'cup'} />
                <FlexboxColumn gap='0.25rem' alignItems='flex-start'>
                  <Flexbox>
                    <ThemedText.H3 weight='semi-bold'>#{formatNumberWithCommas(projectData?.rank)}</ThemedText.H3>
                    {formatTiers(projectData?.top) && <BlueBadge text={`Top ${formatTiers(projectData?.top)}%`} />}
                  </Flexbox>
                  <ThemedText.H6>Overall Contribution Rank</ThemedText.H6>
                </FlexboxColumn>
              </Flexbox>
              <Flexbox gap='0.75rem'>
                <ChainDropdown filter={filters?.chainId} onChange={value => handleFilters('chainId', value)} />
                {/* <ThemeLogo icon={'verticalLine'} /> */}
                {/* <AppDropdown /> */}
                {/* <ThemeLogo icon={'verticalLine'} /> */}
                {/* <DropdownFilter heading={'Between Dates'} /> */}
                {/* <SearchBar /> */}
              </Flexbox>
            </Flexbox>

            <PositionAnalysis
              filters={{
                protocolFilter: {
                  protocol: projectName,
                },
              }}
              userAddress={userAddress}
              userData={projectData}
              projectData={protocolMapping?.[projectName]}
            />
            <PaginationTable
              data={positionData}
              itemsPerPage={itemsPerPage}
              handleVicinty={handleVicinty}
              updatePositioningData={getProtocolPositioning}
              vicinity={currentVicinty}
              lastRank={ranks?.maxRank}
              userRank={projectData?.rank}
            />
          </FlexboxColumn>
        </FlexboxColumn>
      </FlexboxColumn>
    </>
  );
};

export default Project;
